/**
 * Authentication Context
 *
 * React Context for server authentication state
 * Based on quickstart.md API specification
 */

import { createContext } from "react";
import type { UserInfo, AvailableRole, AuthState } from "@/types/auth";

/**
 * Authentication Context Value
 *
 * Provides authentication state and actions to components
 */
export interface AuthContextValue {
  // Authentication state
  /** True if user has valid server session */
  isLoggedIn: boolean;
  /** True during LINE token exchange */
  isAuthenticating: boolean;
  /** Current authentication state machine state */
  authState: AuthState;

  // User information
  /** Cached user profile */
  user: UserInfo | null;
  /** Current user ID (derived from user.id for convenience) */
  userId: string | null;
  /** Roles user can assume */
  availableRoles: AvailableRole[];
  /** Currently active role */
  currentRole: AvailableRole | null;

  // Token information (for debugging)
  /** Current access token */
  accessToken: string | null;
  /** Token expiration timestamp (Unix ms) */
  tokenExpiry: number | null;

  // Error state
  /** Current error if any */
  error: Error | null;

  // Actions
  /** Logout and clear session */
  logout: () => Promise<void>;
  /** Switch to different role */
  switchRole: (roleId: string) => Promise<void>;
}

/**
 * Default context value (throws error if used outside provider)
 */
const defaultContextValue: AuthContextValue = {
  isLoggedIn: false,
  isAuthenticating: false,
  authState: "LOGGED_OUT",
  user: null,
  userId: null,
  availableRoles: [],
  currentRole: null,
  accessToken: null,
  tokenExpiry: null,
  error: null,
  logout: async () => {
    throw new Error("useAuth must be used within AuthProvider");
  },
  switchRole: async () => {
    throw new Error("useAuth must be used within AuthProvider");
  },
};

/**
 * Authentication Context
 *
 * Use via `useAuth()` hook
 */
export const AuthContext = createContext<AuthContextValue>(defaultContextValue);
